import React from 'react';
import Icon from '../../../components/AppIcon';

const UranianPointsSection = ({ data, isVisible, onToggle }) => {
  if (!data) return null;

  const { fatePoints = [], hypotheticalPlanets = [] } = data;

  return (
    <section id="uranian-points" className="scroll-mt-20">
      <div className="bg-card border border-border rounded-lg shadow-brand-md overflow-hidden">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-muted transition-smooth"
          aria-expanded={isVisible}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center mr-3">
              <Icon name="Sparkles" size={20} color="var(--color-warning)" />
            </div>
            <div className="text-left">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                จุดยูเรเนียน
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">Uranian Points</p>
            </div>
          </div>
          <Icon 
            name={isVisible ? "ChevronUp" : "ChevronDown"} 
            size={24} 
            color="var(--color-muted-foreground)"
          />
        </button>

        {isVisible && (
          <div className="border-t border-border">
            {/* Fate Points Section */}
            <div className="p-4 md:p-6 border-b border-border">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                จุดชะตา
                <span className="block text-xs text-muted-foreground font-normal">Fate Points</span>
              </h3>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        จุด
                        <span className="block text-xs text-muted-foreground normal-case">Point</span>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        องศา
                        <span className="block text-xs text-muted-foreground normal-case">Degree</span>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        ราศี
                        <span className="block text-xs text-muted-foreground normal-case">Zodiac Sign</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {fatePoints?.map((point, index) => (
                      <tr key={index} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-6 py-4 text-sm font-medium text-foreground">
                          {point?.nameTh}
                          <span className="block text-xs text-muted-foreground">{point?.nameEn}</span>
                        </td>
                        <td className="px-6 py-4 text-sm data-text text-foreground">
                          {point?.degree}°
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {point?.zodiacTh}
                          <span className="block text-xs text-muted-foreground">{point?.zodiacEn}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {fatePoints?.map((point, index) => (
                  <div key={index} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">{point?.nameTh}</h4>
                        <p className="text-xs text-muted-foreground">{point?.nameEn}</p>
                      </div>
                      <p className="text-sm data-text font-medium text-foreground">{point?.degree}°</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ราศี (Zodiac)</p>
                      <p className="text-sm text-foreground">{point?.zodiacTh}</p>
                      <p className="text-xs text-muted-foreground">{point?.zodiacEn}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hypothetical Planets Section */}
            <div className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
                ดาวสมมติ
                <span className="block text-xs text-muted-foreground font-normal">Hypothetical Planets</span>
              </h3>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        ดาว
                        <span className="block text-xs text-muted-foreground normal-case">Planet</span>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        องศา
                        <span className="block text-xs text-muted-foreground normal-case">Degree</span>
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                        ราศี
                        <span className="block text-xs text-muted-foreground normal-case">Zodiac Sign</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {hypotheticalPlanets?.map((planet, index) => (
                      <tr key={index} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-6 py-4 text-sm font-medium text-foreground">
                          {planet?.nameTh}
                          <span className="block text-xs text-muted-foreground">{planet?.nameEn}</span>
                        </td>
                        <td className="px-6 py-4 text-sm data-text text-foreground">
                          {planet?.degree}°
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {planet?.zodiacTh}
                          <span className="block text-xs text-muted-foreground">{planet?.zodiacEn}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {hypotheticalPlanets?.map((planet, index) => (
                  <div key={index} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">{planet?.nameTh}</h4>
                        <p className="text-xs text-muted-foreground">{planet?.nameEn}</p>
                      </div>
                      <p className="text-sm data-text font-medium text-foreground">{planet?.degree}°</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ราศี (Zodiac)</p>
                      <p className="text-sm text-foreground">{planet?.zodiacTh}</p>
                      <p className="text-xs text-muted-foreground">{planet?.zodiacEn}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UranianPointsSection;